/**
 * Session Detail Page
 * 
 * Detailed analytics view for a specific reading session showing:
 * - Session timeline and page-by-page breakdown
 * - Cue interactions and timing analysis
 * - Print behavior tracking
 * - Navigation flow visualization
 * - Interaction heatmap and engagement metrics
 * - Detailed session metadata
 */

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft,
  Clock,
  BookOpen,
  MousePointer,
  Play,
  Calendar,
  Monitor,
  MapPin,
  Zap,
  Eye,
  Timer,
  Activity,
  Target,
  Printer,
  Navigation,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Leaf,
  Rainbow,
  Shield,
  Sparkles,
  Heart,
  Coffee
} from "lucide-react";
import { client } from "@/lib/api";
import { toast } from "sonner";
import { getButtonLabel } from "@/lib/button-mapping";

// Session detail interface
interface SessionDetail {
  session: {
    id: string;
    user_id: string;
    book_id: string;
    session_start: string;
    session_end?: string;
    total_duration: number;
    pages_visited: number[];
    interactions_count: number;
    device_type: string;
    browser_info: string;
  };
  page_engagements: Array<{
    id: string;
    page_number: number;
    chapter_name?: string;
    time_on_page: number;
    actual_time_seconds: number;
    page_type: "story" | "cue" | "activity" | "navigation" | "other";
    navigation_source: "toc" | "chapter_nav" | "spread_nav" | "breadcrumb" | "home_button" | "direct_url" | "other";
    interactions: Array<{
      type: string;
      element: string;
      timestamp: string;
      metadata?: any;
    }>;
    cue_interactions: Array<{
      cue_name: string;
      cue_icon: string;
      time_before_click: number;
      click_timestamp: string;
      completion_status: "started" | "completed" | "abandoned";
    }>;
    print_clicks: number;
    print_targets: string[];
    scroll_depth: number;
    completion_status: string;
  }>;
  user_info: {
    name: string;
    email: string;
  };
  session_metrics: {
    engagement_score: number;
    pages_completed: number;
    cues_interacted: number;
    total_print_actions: number;
    average_time_per_page: number;
    navigation_efficiency: number;
  };
}

// Cue icon mapping
const cueIcons = {
  "Golden Leaf": Leaf,
  "Rainbow Tail": Rainbow,
  "Bravery Badge": Shield,
  "Sparkling Petal": Sparkles,
  "Gratitude Leaf": Heart,
  "Dew Cup": Coffee
};

// Page type colors
const pageTypeColors = {
  story: "bg-blue-100 text-blue-700",
  cue: "bg-green-100 text-green-700",
  activity: "bg-purple-100 text-purple-700",
  navigation: "bg-gray-100 text-gray-700",
  other: "bg-orange-100 text-orange-700"
};

export default function SessionDetail() {
  const { userId, sessionId } = useParams<{ userId: string; sessionId: string }>();
  const [sessionDetail, setSessionDetail] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  
  // 🎯 NEW: Collapsible panel states
  const [timelineExpanded, setTimelineExpanded] = useState(true);
  const [interactionExpanded, setInteractionExpanded] = useState(true);

  useEffect(() => {
    if (userId && sessionId) {
      loadSessionDetail();
    }
  }, [userId, sessionId]);

  const loadSessionDetail = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      console.log(`🔄 Loading session detail for userId: ${userId}, sessionId: ${sessionId}`);

      // FIXED: Proper API path structure for session detail
      const response = await client.api.admin["session-detail"][":userId"][":sessionId"].$get(
        { param: { userId: userId!, sessionId: sessionId! } },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log("✅ Session detail loaded:", data);
        setSessionDetail(data);
      } else {
        const errorText = await response.text();
        console.error("❌ Failed to load session details:", response.status, errorText);
        toast.error(`Failed to load session details: ${response.status}`);
      }
    } catch (error) {
      console.error("❌ Session detail load error:", error);
      toast.error("Failed to load session details - network error");
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    return `${minutes}m ${secs}s`;
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded animate-pulse" />
          ))}
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-48 bg-gray-200 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!sessionDetail) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Session details not found</p>
        <Button asChild className="mt-4">
          <Link to={`/admin/user-analytics/${userId}`}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to User Analytics
          </Link>
        </Button>
      </div>
    );
  }

  const { session, page_engagements, user_info, session_metrics } = sessionDetail;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/admin/user-analytics/${userId}`}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Session Detail</h1>
            <p className="text-gray-600">
              {user_info.name} • {formatDate(session.session_start)} at {formatTime(session.session_start)}
            </p>
          </div>
        </div>
        
        <Badge 
          variant={session_metrics.engagement_score >= 80 ? "default" : "secondary"}
          className="text-sm"
        >
          {session_metrics.engagement_score}% Engagement
        </Badge>
      </div>

      {/* Session Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-gray-600">Duration</CardTitle>
              <Clock className="w-4 h-4 text-gray-400" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {formatDuration(session.total_duration)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {formatTime(session.session_start)} - {session.session_end ? formatTime(session.session_end) : 'Ongoing'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-gray-600">Pages Visited</CardTitle>
              <BookOpen className="w-4 h-4 text-gray-400" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {session.pages_visited.length}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {session_metrics.pages_completed} completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-gray-600">Interactions</CardTitle>
              <MousePointer className="w-4 h-4 text-gray-400" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {session.interactions_count}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {session_metrics.cues_interacted} cue interactions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-gray-600">Device</CardTitle>
              <Monitor className="w-4 h-4 text-gray-400" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 capitalize">
              {session.device_type}
            </div>
            <p className="text-xs text-gray-500 mt-1 truncate">
              {session.browser_info}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout: Interaction Analysis (LEFT) + Reading Timeline (RIGHT) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ✅ SWAPPED: Interaction Analysis now on LEFT */}
        <Card>
          <CardHeader 
            className="cursor-pointer hover:bg-gray-50 transition-colors" 
            onClick={() => setInteractionExpanded(!interactionExpanded)}
          >
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Interaction Analysis</CardTitle>
                <CardDescription>
                  Detailed breakdown of user engagement patterns
                </CardDescription>
              </div>
              {interactionExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </div>
          </CardHeader>
          {interactionExpanded && (
            <CardContent className="space-y-6">
              {/* Cue Interactions */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Interactive Cues ({session_metrics.cues_interacted})
                </h4>
                <div className="space-y-2">
                  {page_engagements
                    .filter(page => page.cue_interactions.length > 0)
                    .slice(0, 5)
                    .map((page) => (
                      page.cue_interactions.map((cue, index) => {
                        const IconComponent = cueIcons[cue.cue_name as keyof typeof cueIcons] || Sparkles;
                        return (
                          <div key={`${page.id}-${index}`} className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <IconComponent className="w-4 h-4 text-green-600" />
                              <span className="text-sm text-gray-900">{cue.cue_name}</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-600">
                              <span>Page {page.page_number}</span>
                              <Badge variant="outline" className={`text-xs ${
                                cue.completion_status === 'completed' ? 'bg-green-100 text-green-700' :
                                cue.completion_status === 'started' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {cue.completion_status}
                              </Badge>
                            </div>
                          </div>
                        );
                      })
                    ))
                  }
                </div>
              </div>

              <Separator />

              {/* Print Actions */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Printer className="w-4 h-4" />
                  Print Actions ({session_metrics.total_print_actions})
                </h4>
                <div className="space-y-2">
                  {page_engagements
                    .filter(page => page.print_clicks > 0)
                    .slice(0, 5)
                    .map((page) => (
                      <div key={page.id} className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Printer className="w-4 h-4 text-purple-600" />
                          <span className="text-sm text-gray-900">Page {page.page_number}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-600">
                          <span>{page.print_clicks} clicks</span>
                          <Badge variant="outline" className="text-xs">
                            {page.print_targets.join(', ') || 'Activity'}
                          </Badge>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>

              <Separator />

              {/* Button Click History (NEW - Actual clicks from this session) */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  🖱️ Button Click History ({
                    page_engagements
                      .flatMap(page => page.interactions || [])
                      .filter(int => 
                        (int.type === 'click' || int.type === 'nav_click' || int.type === 'cue_click') && 
                        int.element
                      ).length
                  })
                </h4>
                {(() => {
                  // ✅ FIX #3: Deduplicate button clicks by unique key (element + timestamp)
                  const buttonClicksWithDuplicates = page_engagements
                    .flatMap(page => 
                      (page.interactions || [])
                        .filter(int => 
                          // Include all click types (click, nav_click, cue_click)
                          (int.type === 'click' || int.type === 'nav_click' || int.type === 'cue_click') && 
                          // Must have an element name
                          int.element
                        )
                        .map(int => ({
                          element: int.element,
                          label: getButtonLabel(int.element),
                          timestamp: int.timestamp,
                          page: page.page_number,
                          chapter: page.chapter_name,
                          type: int.type,
                          // Create unique key for deduplication
                          uniqueKey: `${int.element}_${int.timestamp}`
                        }))
                    );

                  // ✅ Deduplicate using Map (keeps first occurrence of each unique interaction)
                  const buttonClicksMap = new Map(
                    buttonClicksWithDuplicates.map(click => [click.uniqueKey, click])
                  );
                  
                  const buttonClicks = Array.from(buttonClicksMap.values())
                    .sort((a, b) => 
                      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
                    );

                  return buttonClicks.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {buttonClicks.map((click, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900 truncate">{click.label}</p>
                            <p className="text-xs text-gray-500">
                              Page {click.page} {click.chapter && `• ${click.chapter}`}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs ml-3">
                            {formatTime(click.timestamp)}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No button clicks recorded in this session</p>
                  );
                })()}
              </div>

              <Separator />

              {/* Navigation Patterns */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Navigation className="w-4 h-4" />
                  Navigation Efficiency
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Efficiency Score</span>
                    <div className="flex items-center gap-2">
                      <Progress value={session_metrics.navigation_efficiency} className="w-16 h-2" />
                      <span className="text-sm font-medium">{session_metrics.navigation_efficiency}%</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Avg Time/Page</span>
                    <span className="text-sm font-medium">{Math.round(session_metrics.average_time_per_page / 60)}m</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {Object.entries(
                      page_engagements.reduce((acc: Record<string, number>, page) => {
                        acc[page.navigation_source] = (acc[page.navigation_source] || 0) + 1;
                        return acc;
                      }, {})
                    ).map(([source, count]) => (
                      <div key={source} className="text-center p-2 bg-gray-50 rounded">
                        <div className="text-sm font-medium">{count}</div>
                        <div className="text-xs text-gray-500 capitalize">{source.replace('_', ' ')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* ✅ SWAPPED: Reading Timeline now on RIGHT */}
        <Card>
          <CardHeader 
            className="cursor-pointer hover:bg-gray-50 transition-colors" 
            onClick={() => setTimelineExpanded(!timelineExpanded)}
          >
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Reading Timeline</CardTitle>
                <CardDescription>
                  Page-by-page progression through the session
                </CardDescription>
              </div>
              {timelineExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </div>
          </CardHeader>
          {timelineExpanded && (
            <CardContent>
              <div className="space-y-3">
                {page_engagements.slice(0, 10).map((page, index) => {
                  const IconComponent = page.cue_interactions.length > 0 && page.cue_interactions[0].cue_name 
                    ? cueIcons[page.cue_interactions[0].cue_name as keyof typeof cueIcons] || Eye
                    : Eye;
                  
                  return (
                    <div key={page.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg">
                      <div className="flex-shrink-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${pageTypeColors[page.page_type] || pageTypeColors.other}`}>
                          <IconComponent className="w-4 h-4" />
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900 text-sm">Page {page.page_number}</p>
                            {page.chapter_name && (
                              <p className="text-xs text-gray-500 truncate">{page.chapter_name}</p>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-3 text-xs text-gray-600">
                            <div className="flex items-center gap-1">
                              <Timer className="w-3 h-3" />
                              {Math.round(page.time_on_page / 60)}m
                            </div>
                            
                            <Badge variant="outline" className="text-xs capitalize">
                              {page.page_type}
                            </Badge>
                            
                            {page.cue_interactions.length > 0 && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
                                Cue!
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <Progress value={Math.min(page.scroll_depth, 100)} className="mt-2 h-1" />
                      </div>
                      
                      {index < page_engagements.length - 1 && (
                        <ChevronRight className="w-3 h-3 text-gray-400" />
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </motion.div>
  );
}
